import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Historico.css';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../authContext';
import ChatHistoryInput from '../../components/ChatHistoryInput/ChatHistoryInput';
import SessionButton from '../../components/SessionButton/SessionButton';
import SkillsManagerModal from '../../components/SkillsManagerModal';
import { performLogout } from '../../utils/logout';
import { enrichHistoryWithActivity } from '../../components/ActivityStepInline/buildActivityFromExecutionEvents';
import { aplicarStatusDoPoll, fundirStatusDeExecucao, limparIndicadorDeSessaoVista } from './helpers';

function Historico({ setChatHistory, classeCSS, newButtonName, isReportMode, setLoading, sessions, setSessions, selectedSession, setSelectedSession, sessionId, setSessionId, user, assistantId, setTotalCost, isMobile = false, createNewChat, closeHistoricoOverlay, summaryInfo, setSummaryInfo, messages, onCreateWithIris = () => { }, onSelectSkill = () => { }, onEditSkillInChat = () => { } }) { // Recebe a função setChatHistory e setClearMessages como prop
    const { t: tChat } = useTranslation('chat');
    const { t: tCommon } = useTranslation('common');
    const [loadingList, setLoadingList] = useState(false); // Estado para controlar o loading
    const { accessToken, setAccessToken, email, resourceGroup } = useContext(AuthContext);
    const [showLogout, setShowLogout] = useState(false);
    const [searchValue, setSearchValue] = useState(''); // Estado para o valor do input de busca
    const [showSkillsManager, setShowSkillsManager] = useState(false); // Tarefa 3.1: shell do modal "Gerenciador de Skills"
    const menuRef = useRef(null);
    const navigate = useNavigate();

    // novo estado para controlar animação de saída
    const [shouldClose, setShouldClose] = useState(false);

    // Função intermediária para disparar animação antes de realmente fechar
    const handleClose = () => {
        // dispara animação no CSS
        setShouldClose(true);
        // após 300ms (duração da animação), chama o método original
        setTimeout(() => {
            closeHistoricoOverlay();
            setShouldClose(false);
        }, 300);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                // Verifica se o clique foi no ícone do usuário
                const userIcon = document.querySelector('.header__user-initial');
                if (userIcon && !userIcon.contains(event.target)) {
                    setShowLogout(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Função para buscar a lista de sessões
    const fetchSessions = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_URL;

        const resource = resourceGroup?.[0];
        const geminiKey = resource?.pdf_processing?.key_gemini ?? null;
        const blobConnection = resource?.pdf_processing?.connection_string;
        const blobContainer = resource?.pdf_processing?.container_name;

        const endpoint = isReportMode
            ? `${baseUrl}/post_list_report`
            : `${baseUrl}/post_list_session`;

        try {
            setLoadingList(true);
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`
                },
                body: JSON.stringify(
                    isReportMode
                        ? { email: user, assistant_id: assistantId }         // para post_list_report
                        : { email: user, assistant_id: assistantId, gemini_key: geminiKey, blob_connection: blobConnection, blob_container: blobContainer } // para get_list_session
                )
            });

            const data = await response.json();
            setLoadingList(false);


            // /post_list_session passou a devolver `status: "processing"` para
            // toda sessão com execução viva no banco (User Story 3,
            // specs/execution_progress/) - antes o indicador dependia de a aba
            // ter presenciado a saída do chat no meio do stream. A fusão com o
            // estado local (regra em ./helpers) é o que preserva o aviso de
            // "ficou pronto", que só existe no cliente.
            const mergeStatus = (list) => (prevSessions) => fundirStatusDeExecucao(list, prevSessions);

            setSessions(mergeStatus(data.id_list));

        } catch (error) {
            console.error('Erro ao buscar sessões:', error);
            setLoadingList(false);
        }
    }, [isReportMode, user, assistantId, accessToken, setSessions]);

    // Etapa 4 (PBI 872): faz polling só das sessões marcadas "processing" -
    // não da lista inteira. Assim que o backend confirma "completed"/"error",
    // atualiza o item na lista e para de perguntar por aquela sessão.
    const pendingSessionIds = sessions
        .filter(s => s.status === 'processing')
        .map(s => s.session_id);
    const pendingSessionIdsKey = pendingSessionIds.join(',');

    useEffect(() => {
        if (isReportMode || pendingSessionIds.length === 0) {
            return;
        }

        const baseUrl = process.env.REACT_APP_API_URL;

        const checkStatus = async () => {
            try {
                const response = await fetch(`${baseUrl}/post_generation_status`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({ assistant_id: assistantId, session_ids: pendingSessionIds })
                });
                const data = await response.json();
                const statuses = data.statuses || {};

                setSessions(prevSessions => aplicarStatusDoPoll(prevSessions, statuses));
            } catch (error) {
                console.error('Erro ao consultar generation_status:', error);
            }
        };

        const intervalId = setInterval(checkStatus, 12000);
        return () => clearInterval(intervalId);
    }, [pendingSessionIdsKey, isReportMode, assistantId, accessToken, setSessions]);

    useEffect(() => {
        if (isReportMode || !sessionId) return;
        if (sessions.some(s => s.session_id === sessionId)) return;

        setSessions([{ label: "New Chat", session_id: sessionId }, ...sessions]);
        setSelectedSession(sessionId);
    }, [sessionId, sessions, setSelectedSession, setSessions, isReportMode]);

    // useEffect para definir o summaryInfo conforme o conteúdo de selectedSession
    useEffect(() => {
        if (!summaryInfo || typeof setSummaryInfo !== 'function') {
            return;
        }

        if (selectedSession && sessions.length > 0) {
            const sessionData = sessions.find(s => s.session_id === selectedSession);
            if (sessionData) {
                // Se o session_id for igual ao já armazenado, apenas preserva o status atual
                if (sessionData.session_id === summaryInfo.session_id) {
                    if (summaryInfo.status === true) {
                        return; // não faz nada, o updateSummary já cuidou disso
                    }
                    setSummaryInfo({
                        label: sessionData.label,
                        session_id: sessionData.session_id,
                        status: summaryInfo.status
                    });
                } else {
                    const isNewOrEmpty = (sessionData.label === '' || sessionData.label === 'New Chat');
                    setSummaryInfo({
                        label: sessionData.label,
                        session_id: sessionData.session_id,
                        status: !isNewOrEmpty
                    });
                }
            }
        }
    }, [selectedSession, sessions]);



    // useEffect para buscar a lista de sessões ao carregar o componente
    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    // Função para buscar o histórico de uma sessão
    const fetchSessionHistory = async (session_id) => {

        // Abrir a conversa consome o aviso de resultado pronto, mas NÃO apaga
        // execução em andamento - ver ./helpers para a distinção entre os dois
        // significados de `status`.
        setSessions(prev => limparIndicadorDeSessaoVista(prev, session_id));

        // se for mobile, fecha o overlay imediatamente (com animação)
        if (isMobile) {
            handleClose();
        }

        const endpoint = isReportMode
            ? `${process.env.REACT_APP_API_URL}/post_find_report`
            : `${process.env.REACT_APP_API_URL}/post_session`;
        try {
            setSelectedSession(session_id);  // Define a sessão selecionada
            setLoading(true);  // Ativa o loading
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    'session_id': session_id,
                    'assistant_id': assistantId
                })
            });
            const data = await response.json();
            setLoading(false); // Desativa o loading
            setChatHistory(enrichHistoryWithActivity(data.history, tChat));  // Atualiza o histórico do chat no componente pai, reconstruindo o pill de atividade
            if (isReportMode && typeof setTotalCost === 'function') {
                const fetchedCost = Number(data.total_cost);
                setTotalCost(Number.isFinite(fetchedCost) ? fetchedCost : 0);
            }
            setSessionId(session_id);
        } catch (error) {
            console.error('Erro ao buscar histórico da sessão:', error);
        }
    };

    const parseSessionId = (sessionId) => {
        if (sessionId == null || typeof sessionId !== 'string') {
            return null;
        }
        const trimmed = sessionId.trim();
        if (!trimmed) {
            return null;
        }
        const [id] = trimmed.split(' ');
        if (!id) {
            return null;
        }
        const parts = id.split('-');
        // Formato esperado: {user}-…-{count}-{year}-{month}-{day} (mín. 5 segmentos após split)
        if (parts.length < 5) {
            return null;
        }
        const day = parts.pop();
        const month = parts.pop();
        const year = parts.pop();
        const count = parts.pop();
        const user = parts.join('-');

        return { user, count, year, month, day };
    };

    // Filtrar sessões de hoje e dos últimos 7 dias
    const today = dayjs().startOf('day');
    const sevenDaysAgo = today.subtract(7, 'day');

    const filteredSessions = sessions.filter(session => {
        if (!session || !session.session_id) return false;
        if (!searchValue.trim()) return true;

        const term = searchValue.toLowerCase();
        const labelMatch = session.label?.toLowerCase().includes(term);
        const idMatch = session.session_id.toLowerCase().includes(term);

        return labelMatch || idMatch;
    });

    const sessionsToday = filteredSessions.filter(session => {
        const parsed = parseSessionId(session.session_id);
        if (!parsed) return false;
        const { year, month, day } = parsed;
        const sessionDate = dayjs(`${year}-${month}-${day}`);
        if (!sessionDate.isValid()) return false;
        return sessionDate.isSame(today, 'day');
    });

    const sessionsLast7Days = filteredSessions.filter(session => {
        const parsed = parseSessionId(session.session_id);
        if (!parsed) return false;
        const { year, month, day } = parsed;
        const sessionDate = dayjs(`${year}-${month}-${day}`);
        if (!sessionDate.isValid()) return false;

        return (
            (sessionDate.isSame(sevenDaysAgo) || sessionDate.isAfter(sevenDaysAgo)) &&
            sessionDate.isBefore(today)
        );
    });

    const sessionsOlder = filteredSessions.filter(session => {
        const parsed = parseSessionId(session.session_id);
        if (!parsed) return false;
        const { year, month, day } = parsed;
        const sessionDate = dayjs(`${year}-${month}-${day}`);
        if (!sessionDate.isValid()) return false;
        return sessionDate.isBefore(sevenDaysAgo);
    });

    const categorizedIds = new Set([
        ...sessionsToday.map((s) => s.session_id),
        ...sessionsLast7Days.map((s) => s.session_id),
        ...sessionsOlder.map((s) => s.session_id),
    ]);
    const sessionsUncategorized = filteredSessions.filter(
        (s) => s.session_id != null && !categorizedIds.has(s.session_id)
    );
    const sessionsOlderCombined = [...sessionsOlder, ...sessionsUncategorized];

    const backToHub = () => {
        navigate('/hub'); // Redireciona para a página /hub
    };

    const handleRenameConfirm = (sessionId, newLabel) => {
        setSessions(prevSessions =>
            prevSessions.map(session =>
                session.session_id === sessionId
                    ? { ...session, label: newLabel }
                    : session
            )
        );
    };

    return (
        <main className={`${classeCSS} ${shouldClose ? 'exit' : ''}`}>
            {isMobile && (
                <div className='mobile-history-header'>
                    <img src="/imagens/iris.svg" alt={tCommon("companyLogo")} style={{ height: 38, marginBottom: 47 }} />

                    <img src="/imagens/x.svg" onClick={handleClose} style={{ position: 'absolute', top: 13, right: 13, width: 15, height: 15 }} alt={tChat("closeIcon")} />

                    <div className='menu-item-history' onClick={backToHub}>
                        <img style={{ height: 18 }} src="/imagens/home.svg" alt={tCommon("home")} />
                        <p style={{ fontSize: 14, fontWeight: '500' }}>{tCommon('home')}</p>
                    </div>

                    <div
                        className='menu-item-history'
                        onClick={() => {
                            const hasUserMessage = messages?.some(msg => msg.role === 'user');
                            if (!hasUserMessage) return; // impede o clique se não houver msg do usuário

                            handleClose();
                            createNewChat();
                        }}>
                        <img style={{ height: 18 }} src="/imagens/new_chat_icon.svg" alt={tChat("newChatIcon")} />
                        <p style={{ fontSize: 14, fontWeight: '500' }}>{newButtonName}</p>
                    </div>

                    <hr />

                </div>
            )}

            {!isReportMode && !isMobile && (
                <section className='novo-chat'>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            const hasUserMessage = messages?.some(msg => msg.role === 'user');
                            if (!hasUserMessage) return; // impede o clique se não houver msg do usuário

                            createNewChat();
                        }}
                        data-testid='newchat'>
                        <img src="/imagens/new_chat_icon.svg" alt={tChat("newChatIcon")} />
                        {newButtonName}
                    </Button>
                </section>
            )}

            {!isReportMode && !isMobile && (
                <section className='manage-skills'>
                    <Button
                        onClick={() => setShowSkillsManager(true)}
                        data-testid='manage-skills'>
                        <img src="/imagens/skills-icon.svg" alt="" />
                        {tChat('manage-skills')}
                    </Button>
                </section>
            )}

            <ChatHistoryInput
                value={searchValue}
                onChange={setSearchValue}
                placeholder={tChat('search_history')}
            />

            {loadingList ? (
                <div className="loading-container-historico">
                    <CircularProgress /> {/* Ícone de loading */}
                </div>
            ) : (
                <section className='lista-chats'>


                    <div className="today-sessions">
                        <h4>{tChat('today')}</h4>
                        {sessionsToday.map((session, index) => (
                            <SessionButton
                                key={session.session_id}
                                session={session}
                                isSelected={selectedSession === session.session_id}
                                onSessionClick={fetchSessionHistory}
                                onRenameConfirm={handleRenameConfirm}
                                assistantId={assistantId}
                                accessToken={accessToken}
                                dataTestId="chat-today"
                                isMobile={isMobile}
                            />
                        ))}
                    </div>

                    <hr />

                    <div className="last-7-days-sessions">
                        <h4>{tChat('days_ago')}</h4>
                        {sessionsLast7Days.map((session, index) => (
                            <SessionButton
                                key={session.session_id}
                                session={session}
                                isSelected={selectedSession === session.session_id}
                                onSessionClick={fetchSessionHistory}
                                onRenameConfirm={handleRenameConfirm}
                                assistantId={assistantId}
                                accessToken={accessToken}
                                dataTestId="chat-before"
                                isMobile={isMobile}
                            />
                        ))}
                    </div>

                    <hr />

                    <div className="older-sessions">
                        <h4>{tChat('older')}</h4>
                        {sessionsOlderCombined.map((session, index) => (
                            <SessionButton
                                key={session.session_id ?? `session-${index}`}
                                session={session}
                                isSelected={selectedSession === session.session_id}
                                onSessionClick={fetchSessionHistory}
                                onRenameConfirm={handleRenameConfirm}
                                assistantId={assistantId}
                                accessToken={accessToken}
                                dataTestId="chat-older"
                                isMobile={isMobile}
                            />
                        ))}
                    </div>

                </section>
            )}


            {isMobile && (
                <div className="involucro-userH">
                    {showLogout && (
                        <div className="user-menuH" ref={menuRef}>
                            <button
                                className="user-menu-itemH"
                                onClick={() => performLogout(navigate)}
                            >
                                {tCommon('sair')}
                            </button>
                        </div>
                    )}
                    <button
                        className="header__user-initialH"
                        onClick={() => setShowLogout(!showLogout)}
                    >
                        {email?.charAt(0)?.toUpperCase()}
                    </button>

                </div>
            )}

            <SkillsManagerModal
                isOpen={showSkillsManager}
                onClose={() => setShowSkillsManager(false)}
                email={email}
                onCreateWithIris={onCreateWithIris}
                onSelectSkill={onSelectSkill}
                onEditSkillInChat={onEditSkillInChat}
            />
        </main>
    )
}

export default Historico